import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios'
import { NextPaths } from '../router/next-paths'
import { IMPERSONATION_TOKEN_KEY } from '../utils/impersonationHandoff'
export const axiosClient = axios.create({
  withCredentials: true,
})

// Add request interceptor to include authorization header
axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const impersonationToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
    const token = impersonationToken || localStorage.getItem('token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

// Add response interceptor to handle authentication errors
axiosClient.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      const originalRequest = error.config

      // A 401 from a pre-authentication endpoint (request/verify login code,
      // signup, email or MFA login verification, OAuth session login, invite
      // acceptance) means "bad credentials / wrong code" — NOT an expired
      // access token. Attempting a token refresh there is nonsensical: it
      // fires a refresh against the customer token endpoint (often with a
      // stale, unrelated refresh token and no client_id), which fails with a
      // 400 and gets surfaced by the Next.js dev overlay. Let the original 401
      // bubble straight up so the auth UI can show "invalid code".
      const requestUrl: string = originalRequest?.url || ''
      const isPreAuthEndpoint = [
        '/auth/logins',
        '/auth/signups',
        '/auth/email-verifications',
        '/auth/two-factor-login-verifications',
        '/auth/accepted-invites',
        '/auth/google/session-logins',
        '/auth/github/session-logins',
      ].some(path => requestUrl.includes(path))

      // Prevent infinite loops
      if (!isPreAuthEndpoint && !originalRequest._retry) {
        originalRequest._retry = true

        // Skip refresh for impersonation sessions — they have no refresh token
        const isImpersonating = !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
        const refreshToken = isImpersonating ? null : localStorage.getItem('refreshToken')

        // Check if we have a refresh token and if it's worth trying to refresh
        if (refreshToken) {
          try {
            // Use a clean axios instance to avoid interceptor loops
            // Call the token endpoint relative to the current origin to go through the Next.js proxy
            // The proxy in middleware or next.config.js will handle /v1/customer/auth/token -> backend
            const isBrowser = typeof window !== 'undefined'
            let tokenUrl = '/v1/customer/auth/token'

            if (!isBrowser) {
              tokenUrl = `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1'}/customer/auth/token`
            }

            const response = await axios.post(
              tokenUrl,
              new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
                // Public clients don't need client_secret usually, but if needed it should be here
              }),
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
              },
            )

            const { access_token, refresh_token: newRefreshToken, expires_in } = response.data

            if (access_token) {
              // Update stored tokens
              localStorage.setItem('token', access_token)
              if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken)
              if (expires_in) {
                const expiresAt = Date.now() + expires_in * 1000
                localStorage.setItem('token_expires_at', expiresAt.toString())
              }

              // Update authorization header
              axiosClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
              originalRequest.headers['Authorization'] = `Bearer ${access_token}`

              // Retry original request
              return axiosClient(originalRequest)
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
            // Clear tokens on failed refresh
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('token_expires_at')
            // Let the error bubble up so the UI can handle it (e.g. show login modal)
          }
        }
      }

      // 401 Unauthorized error - let the application handle with toast messages
      // Don't redirect or show error pages, just let the error bubble up to be handled by useErrorHandler
    }

    // Handle 403 Forbidden errors - redirect to login with return URL
    if (error.response?.status === 403) {
      // Don't redirect if we're on auth-related pages or public customer pages
      const authPages = [
        NextPaths.login,
        '/login/',
        '/auth/callback',
        '/auth/cli-init',
        '/auth/customer/consent',
        '/auth/mcp-connect',
        '/auth/provider-mcp',
        '/auth/register',
        '/register',
      ]
      const customerPages = ['/customer/checkout', '/customer/manage']
      const path = typeof window !== 'undefined' ? window.location.pathname : ''
      const isOnAuthPage = path ? authPages.some(page => path.includes(page)) : false
      const isOnCustomerPage = path ? customerPages.some(p => path.startsWith(p)) : false
      const isOnOperationPage = path.startsWith('/operation')

      // Check if we're already on the login page, auth pages, or customer pages to prevent redirect loop
      if (
        typeof window !== 'undefined' &&
        !isOnAuthPage &&
        !isOnCustomerPage &&
        !isOnOperationPage
      ) {
        // Store the current URL to redirect back after login
        const currentPath = window.location.pathname + window.location.search
        const loginUrl = `${NextPaths.login}?returnUrl=${encodeURIComponent(currentPath)}`
        window.location.href = loginUrl
      }
      // If we're on an auth or customer page, don't redirect - let the component handle the error
    }

    // Log 500 errors for debugging
    if (error.response?.status === 500) {
      console.error('500 Server error:', error.config?.url, error.response?.data)
    }

    return Promise.reject(error)
  },
)

const RETRYABLE_METHODS = new Set(['get', 'head'])
const TRANSIENT_RETRY_DELAYS_MS = [300, 700, 1500] as const

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const isTransientGatewayError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  if (!error.response) {
    return true
  }

  const status = error.response.status
  if (status === 502 || status === 503 || status === 504) {
    return true
  }

  const code = error.code
  return code === 'ECONNRESET' || code === 'ECONNABORTED' || code === 'ERR_NETWORK'
}

const executeWithRetry = async (
  config: Parameters<typeof axiosClient.request>[0],
  method: string,
) => {
  const normalizedMethod = method.toLowerCase()
  if (!RETRYABLE_METHODS.has(normalizedMethod)) {
    return axiosClient.request(config)
  }

  let lastError: unknown
  for (let attempt = 0; attempt <= TRANSIENT_RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await axiosClient.request(config)
    } catch (error) {
      lastError = error
      const hasRetriesLeft = attempt < TRANSIENT_RETRY_DELAYS_MS.length
      if (!hasRetriesLeft || !isTransientGatewayError(error)) {
        throw error
      }
      await sleep(TRANSIENT_RETRY_DELAYS_MS[attempt])
    }
  }

  throw lastError
}

const request = async (
  url: string,
  method: string,
  data?: Record<string, unknown>,
  headers?: AxiosHeaders | Record<string, string>,
) => {
  url = url.startsWith('/') ? url : `/${url}`

  // In browser (client-side), use relative URLs that go through Next.js proxy
  // This avoids CORS issues when frontend is on different origin (e.g., ngrok)
  const isBrowser = typeof window !== 'undefined'

  if (isBrowser) {
    // Use Next.js proxy rewrite: /v1/* -> backend
    // Add /ui prefix for frontend API calls, but not for auth or analytics endpoints
    const passthroughPrefixes = [
      '/ui/',
      '/auth/',
      '/provider-mcp/',
      '/analytics/',
      '/provider/analytics/',
      '/customer/',
      '/mcp/',
      '/operations/',
    ]
    let apiUrl = passthroughPrefixes.some(p => url.startsWith(p)) ? url : `/ui${url}`

    // Ensure /v1 prefix for proxy (except for /ui which already handles it)
    // Note: The rewrite rule in next.config.js will handle /ui/ requests too
    if (!apiUrl.startsWith('/ui') && !apiUrl.startsWith('/v1')) {
      apiUrl = `/v1${apiUrl}`
    }

    // Use relative URL - Next.js will proxy it via rewrites
    const fullURL = apiUrl

    return executeWithRetry(
      {
        url: fullURL,
        method,
        data,
        headers,
      },
      method,
    )
  } else {
    // Server-side: use full URL
    const baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1'
    const uiUrl = url.startsWith('/ui/')
      ? url
      : url.startsWith('/auth/')
        ? url
        : url.startsWith('/provider-mcp/')
          ? url
          : url.startsWith('/analytics/')
            ? url
            : url.startsWith('/provider/analytics/')
              ? url
              : url.startsWith('/customer/')
                ? url
                : `/ui${url}`
    const fullURL = `${baseURL}${uiUrl}`

    return executeWithRetry(
      {
        url: fullURL,
        method,
        data,
        headers,
      },
      method,
    )
  }
}

export const post = async (url: string, payload?: {}) => {
  return request(url, 'post', payload)
}

export const postWithHeaders = async (
  url: string,
  payload?: Record<string, unknown>,
  headers?: AxiosHeaders | Record<string, string>,
) => {
  return request(url, 'post', payload, headers)
}

export const postMultiForm = async (url: string, payload?: {}) => {
  return request(url, 'post', payload)
}

export const put = async (url: string, payload?: {}) => {
  return request(url, 'put', payload)
}

export const putMultiForm = async (url: string, payload?: {}) => {
  return request(url, 'put', payload)
}

export const patch = async (url: string, payload?: {}) => {
  return request(url, 'patch', payload)
}

export const get = async (url: string) => {
  return request(url, 'get', {})
}

export type SearchParamValue = string | number | boolean | undefined | null

/**
 * Build a `URLSearchParams` from a plain object, skipping `undefined`/`null`/''
 * values. Single source of truth for the "append non-empty query params" logic
 * that was reimplemented per hook across both surfaces.
 */
export const buildSearchParams = (params: Record<string, SearchParamValue>): URLSearchParams => {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      sp.append(key, String(value))
    }
  }
  return sp
}

/**
 * Typed GET returning the response body. Accepts either a prebuilt
 * `URLSearchParams` or a plain params object (run through
 * {@link buildSearchParams}).
 */
export const apiGet = async <T>(
  url: string,
  params?: URLSearchParams | Record<string, SearchParamValue>,
): Promise<T> => {
  const sp =
    params instanceof URLSearchParams ? params : params ? buildSearchParams(params) : undefined
  const qs = sp?.toString()
  const response = await get(`${url}${qs ? `?${qs}` : ''}`)
  return response.data as T
}

export const del = async (url: string, payload?: {}) => {
  return request(url, 'delete', payload)
}
